/**
 * Drag-payload validation for the report canvas (Story 37.7, AC #5).
 *
 * The palette serialises a widget/preset drag as JSON into the drag event's
 * `text/plain` data. `handleDrop` used to `JSON.parse` it inside a bare
 * `try { … } catch {}` — so a malformed, truncated, or foreign payload was
 * silently swallowed and the drop did nothing, with no feedback. This module
 * parses + structurally validates the payload so a bad drop surfaces a toast
 * instead of vanishing. A VALID payload behaves exactly as before.
 *
 * No new dependency: a hand validator (typed guard) matches the existing typed
 * shape `{ type, colSpan, rowSpan, integrationId?, platform? }` — Zod is not an
 * apps/web dependency and isn't warranted for one tiny shape.
 */

/** The validated drag payload the canvas understands. */
export interface DragPayload {
  type: string;
  colSpan: number;
  rowSpan: number;
  integrationId?: string;
  platform?: string;
}

/** Discriminated parse result so the caller can show a precise message. */
export type DragPayloadResult =
  | { ok: true; payload: DragPayload }
  | { ok: false; reason: string };

function isPositiveInteger(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v) && v > 0;
}

/** The grid is 12 columns wide, so a `colSpan` over 12 is malformed. */
const MAX_COL_SPAN = 12;
/** Rows are unbounded; a generous ceiling only rejects absurd `rowSpan` values
 *  (review R2-4: do NOT reuse the 12-column bound for the row axis). */
const MAX_ROW_SPAN = 1000;

/**
 * Parses + validates a raw drag-payload string. Returns a discriminated result:
 * `{ ok: true, payload }` for a well-formed payload, or `{ ok: false, reason }`
 * with a human-readable reason for the toast.
 *
 * Validation rules:
 * - must be valid JSON for an object
 * - `type` must be a non-empty string, AND (when `isKnownType` is supplied —
 *   review finding #3) a recognised palette/preset type; an unknown type is
 *   rejected with a toast rather than committing a broken `undefined`-titled
 *   widget
 * - `colSpan` must be a positive integer ≤ 12 (grid width); `rowSpan` a
 *   positive integer within a generous ceiling (review findings #5, R2-4) —
 *   a 0/negative/fractional/absurd span is malformed
 * - `integrationId` / `platform`, when present, must be strings
 *
 * @param raw         the drag event's `text/plain` data
 * @param isKnownType optional predicate that recognises valid widget/preset
 *                    types (the caller owns the type catalogue to keep this a
 *                    dependency-free leaf module)
 */
export function parseDragPayload(
  raw: string | null | undefined,
  isKnownType?: (type: string) => boolean,
): DragPayloadResult {
  if (!raw) return { ok: false, reason: "Empty drag payload" };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, reason: "Could not read the dragged item" };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, reason: "Dragged item is not a valid widget" };
  }

  const p = parsed as Record<string, unknown>;

  if (typeof p.type !== "string" || p.type.length === 0) {
    return { ok: false, reason: "Dragged item is missing a widget type" };
  }
  if (isKnownType && !isKnownType(p.type)) {
    return { ok: false, reason: "Dragged item is not a recognised widget" };
  }
  if (
    !isPositiveInteger(p.colSpan) || p.colSpan > MAX_COL_SPAN ||
    !isPositiveInteger(p.rowSpan) || p.rowSpan > MAX_ROW_SPAN
  ) {
    return { ok: false, reason: "Dragged item has an invalid size" };
  }
  if (p.integrationId !== undefined && typeof p.integrationId !== "string") {
    return { ok: false, reason: "Dragged item has an invalid integration" };
  }
  if (p.platform !== undefined && typeof p.platform !== "string") {
    return { ok: false, reason: "Dragged item has an invalid platform" };
  }

  return {
    ok: true,
    payload: {
      type: p.type,
      colSpan: p.colSpan,
      rowSpan: p.rowSpan,
      integrationId: p.integrationId as string | undefined,
      platform: p.platform as string | undefined,
    },
  };
}
